import React, { useState } from 'react'
import { withStyles } from '@mui/styles'
import { TextField, Typography, Autocomplete } from '@mui/material'

import { ListboxComponent } from './virtualized-list-box.component'
import { filterAutocompleteStyles } from './filter-autocomplete.styles'

/**
 * @typedef {object} FilterAutocompleteProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {string} name - Nombre del campo para identificarlo en el evento onChange.
 * @property {string} [placeholder=''] - Texto placeholder del input.
 * @property {function(FilterAutocompleteChangeEvent):void} onChange - Función invocada al cambiar la selección.
 * @property {Array<{key: string|number, value: string, special?: boolean}>} [values=[]] - Array de opciones disponibles.
 * @property {string|number} [value] - Valor seleccionado actualmente (key de la opción).
 * @property {string} [label] - Etiqueta mostrada antes del input.
 */

/**
 * @typedef {object} FilterAutocompleteChangeEvent
 * @property {object} target - Objeto que contiene información del campo.
 * @property {string} target.name - Nombre del campo.
 * @property {string|number} target.value - Valor seleccionado (key de la opción) o string vacío.
 */

/**
 * Campo autocomplete con virtualización para filtros con muchas opciones.
 *
 * Utiliza un listbox virtualizado para manejar eficientemente listas grandes de opciones.
 * Incluye filtrado por texto, selección automática, y formato de evento compatible con inputs estándar.
 *
 * @component
 * @example
 * const options = [
 *   { key: 1, value: 'Opción 1' },
 *   { key: 2, value: 'Opción 2' },
 *   { key: 3, value: 'Opción 3', special: true }
 * ];
 * <FilterAutocomplete
 *   name="filtro"
 *   label="Seleccionar"
 *   value="1"
 *   values={options}
 *   onChange={(e) => console.log(e.target.value)}
 *   placeholder="Buscar..."
 * />
 *
 * @param {FilterAutocompleteProps} props - Las props del componente FilterAutocomplete.
 */
//Arreglar esto como se arreglo el autocomplete input
export const FilterAutocomplete = withStyles(filterAutocompleteStyles)(({
  classes,
  name,
  placeholder = '',
  onChange,
  values = [],
  ...props
}) => {
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)

  const value =
    props.value && values.length ? values.find((item) => item.key.toString() === props.value) : null

  const filterOptions = (options, state) => {
    const { inputValue, getOptionLabel } = state
    const value = inputValue.trim().toLowerCase()
    return options.filter((option) => {
      if (option.special) {
        return true
      }
      const optionLabel = getOptionLabel(option)
      if (!optionLabel || typeof optionLabel !== 'string' || optionLabel.trim() === '') {
        return false
      }
      return optionLabel.toLowerCase().includes(value)
    })
  }

  const handleChange = (e, option) => {
    onChange({ target: { name, value: option ? option.key : '' } })
  }

  return (
    <Autocomplete
      id={`${name}-autocomplete-input`}
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={values}
      getOptionLabel={(option) => option.value}
      renderOption={(option) => option.value}
      onChange={handleChange}
      value={value}
      autoSelect
      ListboxComponent={ListboxComponent}
      filterOptions={filterOptions}
      renderInput={(params) => (
        <TextField
          {...params}
          className={classes.root}
          variant="outlined"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            className: classes.input,
            startAdornment: (
              <Typography variant="body1" className={classes.title}>
                {props.label}:
              </Typography>
            ),
          }}
        />
      )}
    />
  )
})
